package com.mgacreative.hotelru.feature.reservation.ui.checkout

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.shared.ui.designsystem.components.HFCard
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

data class CheckOutItem(
    val guestName: String,
    val rooms: List<String>,
    val dateRange: String,
    val status: String
)

@Composable
fun CheckOutListScreen(
    onNavigate: (String) -> Unit = {},
    viewModel: CheckOutViewModel = koinViewModel()
) {
    val checkOuts by viewModel.checkOuts.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.loadTodayCheckOuts()
    }
    
    var selectedReservationId by remember { mutableStateOf<String?>(null) }
    val summary by viewModel.summary.collectAsState()

    if (selectedReservationId != null) {
        var newExtraName by remember { mutableStateOf("") }
        var newExtraPrice by remember { mutableStateOf("") }
        var showAddExtra by remember { mutableStateOf(false) }

        val folioDialogScrollState = rememberLazyListState()

        AlertDialog(
            onDismissRequest = { selectedReservationId = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = { Text(l10n("Folyo Detayı")) },
            text = {
                LazyColumn(
                    state = folioDialogScrollState,
                    modifier = Modifier.fillMaxWidth().heightIn(max = 300.dp).verticalScrollbar(folioDialogScrollState)
                ) {
                    item {
                        Text("${l10n("Konaklama")}: ${summary.nights} ${l10n("gece")} x ${summary.roomRate} ₽")
                        HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                        Text(l10n("Ekstralar:"), fontWeight = FontWeight.Bold)
                    }
                    items(summary.extras) { extra ->
                        Row(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text(extra.name)
                            Text("${extra.price} ₽")
                        }
                    }
                    item {
                        HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text(l10n("TOPLAM"), fontWeight = FontWeight.Bold)
                            Text("${summary.total} ₽", fontWeight = FontWeight.Bold)
                        }
                    }
                    if (showAddExtra) {
                        item {
                            Spacer(modifier = Modifier.height(16.dp))
                            OutlinedTextField(
                                value = newExtraName,
                                onValueChange = { newExtraName = it },
                                label = { Text(l10n("Açıklama")) },
                                modifier = Modifier.fillMaxWidth()
                            )
                            OutlinedTextField(
                                value = newExtraPrice,
                                onValueChange = { newExtraPrice = it },
                                label = { Text(l10n("Tutar") + " (₽)") },
                                modifier = Modifier.fillMaxWidth()
                            )
                            Button(
                                onClick = {
                                    val amount = newExtraPrice.toDoubleOrNull()
                                    val resId = selectedReservationId
                                    if (newExtraName.isNotBlank() && amount != null && resId != null) {
                                        viewModel.addManualFolioItem(resId, newExtraName, amount)
                                        showAddExtra = false
                                        newExtraName = ""
                                        newExtraPrice = ""
                                    }
                                },
                                modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
                            ) {
                                Text(l10n("Ekle"))
                            }
                        }
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { showAddExtra = true }) {
                    Text(l10n("Ekstra Ekle"))
                }
            },
            dismissButton = {
                TextButton(onClick = { selectedReservationId = null }) { Text(l10n("Kapat")) }
            }
        )
    }

    Scaffold(
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        topBar = { CheckOutTopBar() },
        bottomBar = { CheckOutBottomNavBar(onNavigate = onNavigate) }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF1F5F9)) // Corporate subtle gray background
        ) {
            // Date Selector
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFE2E8F0)) // Slightly darker gray for date bar
                    .padding(vertical = 12.dp, horizontal = 16.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Default.KeyboardArrowLeft, contentDescription = l10n("Önceki Gün"), tint = Color(0xFF1E3A8A))
                Text("02/04/2026", fontWeight = FontWeight.Bold, color = Color(0xFF1E3A8A), fontSize = 16.sp)
                Icon(Icons.Default.KeyboardArrowRight, contentDescription = l10n("Sonraki Gün"), tint = Color(0xFF1E3A8A))
            }
            // List of Checkouts
            if (checkOuts.isEmpty()) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text(l10n("Bugün için çıkış yapacak misafir bulunamadı."), color = Color.Gray, fontSize = 16.sp)
                }
            } else {
                val gridState = rememberLazyGridState()
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
                    LazyVerticalGrid(
                        state = gridState,
                        columns = GridCells.Adaptive(minSize = 380.dp),
                        modifier = Modifier.fillMaxSize().widthIn(max = 1400.dp).verticalScrollbar(gridState),
                        contentPadding = PaddingValues(16.dp),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        items(checkOuts) { itemData ->
                            val item = CheckOutItem(
                                guestName = itemData.guestName,
                                rooms = itemData.rooms,
                                dateRange = itemData.dateRange,
                                status = l10n("Çıkış yapıldı")
                            )
                            CheckOutCard(item = item, onClick = {
                                selectedReservationId = itemData.id
                                viewModel.loadFolio(itemData.id)
                            })
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun CheckOutTopBar() {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFF1E3A8A)) // Corporate Navy Blue
            .padding(horizontal = 16.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(Icons.Default.Menu, contentDescription = "Menu", tint = Color.White)
        Spacer(modifier = Modifier.width(16.dp))
        Text(
            text = l10n("Check out"),
            color = Color.White,
            fontSize = 20.sp,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.weight(1f)
        )
        Icon(Icons.Default.Info, contentDescription = l10n("Yardım"), tint = Color.White)
        Spacer(modifier = Modifier.width(16.dp))
        Icon(Icons.Default.Home, contentDescription = l10n("Otel"), tint = Color.White)
    }
}

@Composable
fun CheckOutCard(item: CheckOutItem, onClick: () -> Unit = {}) {
    HFCard(
        modifier = Modifier.fillMaxWidth().clickable { onClick() }
    ) {
        Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
            // Left colored border indicator (Corporate Orange/Rust)
            Box(
                modifier = Modifier
                    .width(4.dp)
                    .fillMaxHeight()
                    .background(Color(0xFFEA580C))
            )
            
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
            ) {
                // Name and Status
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Person, contentDescription = "Guest", tint = Color.Gray, modifier = Modifier.size(20.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(item.guestName, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = Color(0xFF1E293B))
                    }
                    
                    // Status Pill
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(16.dp))
                            .background(Color.Transparent)
                            .border(1.dp, Color(0xFFEA580C), RoundedCornerShape(16.dp))
                            .padding(horizontal = 10.dp, vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(Icons.Default.ExitToApp, contentDescription = "Checkout", tint = Color(0xFFEA580C), modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(item.status, color = Color(0xFFEA580C), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                    }
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                // Rooms
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Home, contentDescription = "Rooms", tint = Color.Gray, modifier = Modifier.size(16.dp))
                    Spacer(modifier = Modifier.width(12.dp))
                    Text(item.rooms.joinToString(", "), color = Color.DarkGray, fontSize = 14.sp)
                }
                
                Spacer(modifier = Modifier.height(8.dp))
                
                // Date
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.DateRange, contentDescription = "Dates", tint = Color.Gray, modifier = Modifier.size(16.dp))
                    Spacer(modifier = Modifier.width(12.dp))
                    Text(item.dateRange, color = Color.Gray, fontSize = 14.sp)
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                // Bottom Room Tags
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    item.rooms.forEach { room ->
                        Text(
                            text = "→ $room",
                            color = Color(0xFFEA580C),
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium,
                            modifier = Modifier
                                .border(1.dp, Color(0xFFFDBA74), RoundedCornerShape(12.dp))
                                .background(Color(0xFFFFF7ED), RoundedCornerShape(12.dp))
                                .padding(horizontal = 10.dp, vertical = 4.dp)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun CheckOutBottomNavBar(onNavigate: (String) -> Unit) {
    NavigationBar(containerColor = Color.White) {
        NavigationBarItem(
            icon = { Icon(Icons.Default.List, null) },
            label = { Text(l10n("Rezervasyon"), fontSize = 10.sp) },
            selected = false,
            onClick = { onNavigate("NewReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.DateRange, null) },
            label = { Text(l10n("Doluluk"), fontSize = 10.sp) },
            selected = false,
            onClick = { onNavigate("ReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.ArrowForward, null) },
            label = { Text(l10n("Giriş yap"), fontSize = 10.sp) },
            selected = false,
            onClick = { onNavigate("CheckInListScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.LocationOn, null) },
            label = { Text(l10n("Kullanımda"), fontSize = 10.sp) },
            selected = false,
            onClick = { onNavigate("InUseRoomsScreen") }
        )
        NavigationBarItem(
            icon = { 
                Box(
                    modifier = Modifier
                        .size(42.dp)
                        .clip(androidx.compose.foundation.shape.CircleShape)
                        .background(Color(0xFFE3F2FD)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(Icons.Default.ExitToApp, null, tint = Color(0xFF1E3A8A))
                }
            },
            label = { Text(l10n("Çıkış yap"), fontSize = 10.sp, color = Color(0xFF1E3A8A)) },
            selected = true,
            colors = NavigationBarItemDefaults.colors(indicatorColor = Color.Transparent),
            onClick = { onNavigate("CheckOutListScreen") }
        )
    }
}
